import {
  SyncedTableNameSchema,
  type Activity,
  type Comment,
  type Label,
  type MutationArgs,
  type MutationName,
  type Notification,
  type NotificationPreference,
  type Project,
  type ProjectMember,
  type SyncedTableName,
  type Task,
  type TaskLabel,
  type TaskSubscription,
  type User,
  type WorkflowState,
} from "@/lib/contract";

/** The row type of every table the client holds a replica of. */
export interface EntityMap {
  users: User;
  projects: Project;
  projectMembers: ProjectMember;
  workflowStates: WorkflowState;
  labels: Label;
  tasks: Task;
  taskLabels: TaskLabel;
  comments: Comment;
  activity: Activity;
  taskSubscriptions: TaskSubscription;
  notifications: Notification;
  notificationPreferences: NotificationPreference;
}

/**
 * Compile-time check that EntityMap and the contract's table list agree. If the
 * backend adds a synced table, this stops compiling until the client knows
 * the row type; a table the client could receive but not type is how a
 * replica quietly fills with rows nothing knows how to read.
 */
type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
export const TABLES_MATCH_CONTRACT = true satisfies Exact<keyof EntityMap, SyncedTableName>;

export type Row<T extends SyncedTableName = SyncedTableName> = EntityMap[T];

/** One table of the replica: rows by id. Readonly, so updates must copy. */
export type TableRows<T extends SyncedTableName> = Readonly<Record<string, EntityMap[T]>>;

export type Tables = { readonly [T in SyncedTableName]: TableRows<T> };

export const TABLE_NAMES: readonly SyncedTableName[] = SyncedTableNameSchema.options;

export function emptyTables(): Tables {
  const tables = {} as Record<SyncedTableName, Record<string, never>>;
  for (const name of TABLE_NAMES) tables[name] = {};
  return tables;
}

/**
 * A change the user made that the server has not yet folded into the replica.
 *
 * `ackedVersion` is set when the server confirms the change. The mutation is
 * kept until the replica reaches that version, not dropped at the
 * acknowledgement itself: the ack usually arrives a few milliseconds before
 * the delta that carries the change, and dropping it on the ack would make the
 * card flick back to its old place for exactly that long.
 */
export interface PendingMutation<N extends MutationName = MutationName> {
  id: string;
  name: N;
  args: MutationArgs<N>;
  createdAt: string;
  ackedVersion: number | null;
}

/**
 *   loading          reading the local replica from IndexedDB
 *   connecting       the socket is opening, or retrying after a drop
 *   syncing          connected and catching up on missed changes
 *   live             caught up; changes arrive as they happen
 *   offline          the connection dropped; local changes queue up
 *   reload-required  the server speaks a newer protocol than this tab
 */
export type ConnectionStatus =
  "loading" | "connecting" | "syncing" | "live" | "offline" | "reload-required";

export interface SyncSession {
  userId: string;
  workspaceId: string;
}
